import styled, { css } from 'styled-components/macro';
import noVideoBackground from 'assets/images/noVideoBackground.png';
import {
  BODY_ORIENTATION,
  BodyOrientationType,
  GRADIENT_TYPES,
} from 'queries/details/usePlaybackDetailsQuery';
import { FlexProps } from 'components/layout';

export type ContainerProps = {
  bgColor?: string;
  headerHeight: string;
  display?: string;
  paddingOnMobile?: string;
  backgroundImage?: string;
};

export const orientationFlexDirectionMap: Record<
  BodyOrientationType,
  FlexProps['flexDirection']
> = {
  [BODY_ORIENTATION.center]: 'column',
  [BODY_ORIENTATION.left]: 'row',
  [BODY_ORIENTATION.right]: 'row-reverse',
};

export const Container = styled.div<ContainerProps>`
  display: ${({ display }) => display || 'flex'};
  flex-wrap: wrap;
  box-sizing: border-box;
  background: ${({ bgColor }) => bgColor || '#00399D'};
  width: 100%;
  min-height: ${({ headerHeight }) => `calc(100vh - ${headerHeight})`};
  align-items: flex-start;
  padding: 30px 15px 15px 15px;
  justify-content: center;

  @media screen and (max-width: 1024px) {
    display: block;
    padding: 20px 0 0;
  }

  @media screen and (max-width: 768px) {
    padding: 20px 20px 84px 20px;
    display: block;
  }
  @media screen and (max-width: 520px) {
    padding: ${({ paddingOnMobile }) => paddingOnMobile};
  }
  ${({ backgroundImage }) =>
    backgroundImage &&
    css`
      background-image: url(${backgroundImage});
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    `}
`;

export const BodyContainer = styled.div<{
  bgColor: string;
  backgroundImage: string;
}>`
  display: block;
  box-sizing: border-box;
  background: ${({ bgColor }) => bgColor || '#00399D'};
  width: 100%;
  justify-content: center;
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-height: calc(100vh - 64px - 96px);

  ${({ backgroundImage }) =>
    backgroundImage &&
    css`
      background-image: url(${backgroundImage});
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    `}
  padding:32px 16px;
`;

export const NoDataContainer = styled.div`
  background-image: url(${noVideoBackground});
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  > div {
    height: 280px;
    display: flex;
    flex-direction: column;
    width: 384px;
    color: #fff;
    padding: 24px;
    border-radius: 4px;

    > span {
      font-size: 22px;
      font-weight: bold;
      display: flex;
      line-height: 1.45;
    }

    > p {
      margin: 8px 0 32px;
      font-size: 14px;
      max-width: 384px;
      line-height: 1.14;
    }
  }
`;

export const InnerContainer = styled.div<{
  bodyBackgroundImage?: string;
  showGradient?: number;
  gradient?: GRADIENT_TYPES;
}>`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  flex: 1;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${({ bodyBackgroundImage, showGradient, gradient }) => {
    if (bodyBackgroundImage) {
      const gradientOverlay =
        showGradient && gradient
          ? gradient === GRADIENT_TYPES.DARK
            ? 'linear-gradient(89.91deg, rgba(0, 0, 0, 0.4) 0.09%, rgba(0, 0, 0, 0) 99.92%), '
            : 'linear-gradient(45deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1)), '
          : '';
      return `${gradientOverlay}url(${bodyBackgroundImage})`;
    }
    return 'none';
  }};

  @media screen and (max-width: 575px) {
    padding: 16px !important;
  }
`;

export const VideoCtaWrapper = styled.div<{
  bodyOrientation?: BodyOrientationType;
}>`
  display: flex;
  position: relative;
  align-items: center;

  /* Dynamic flex-direction based on prop */
  flex-direction: ${({
    bodyOrientation,
  }: {
    bodyOrientation?: BodyOrientationType;
  }) =>
    bodyOrientation ? orientationFlexDirectionMap[bodyOrientation] : 'column'};

  min-width: 620px;
  margin-right: 24px;

  @media screen and (min-width: 2800px) {
    width: 800px;
  }

  @media screen and (max-width: 1042px) {
    margin: 0;
    margin-right: 0;
  }

  @media screen and (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }
`;

export const Column = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;

  @media screen and (max-width: 520px) {
    width: 100%;
    padding-bottom: 0;
  }
`;

export const ReactionContainer = styled.div`
  position: relative;
  width: 100%;
  bottom: 17px;
`;

export const AdditionalTextContainer = styled.div<{
  withBackground?: boolean;
  additionalTextBackgroundColor?: string;
  additionalTextColor: string;
}>`
  color: ${({ additionalTextColor }) => additionalTextColor};
  width: 100%;
  box-sizing: border-box;

  ${({ withBackground, additionalTextBackgroundColor, theme }) =>
    withBackground &&
    css`
      background-color: ${additionalTextBackgroundColor ||
      theme.colors.white[100]};
      padding: 10px;
      border-radius: 4px;
      border: 1px solid #e6e9ee;
    `}
`;

export type OverlayWrapperProps = {
  isMinimized?: boolean;
};

export const OverlayIconWrapper = styled.div<OverlayWrapperProps>`
  display: flex;
  width: 100%;
  justify-content: right;
  align-items: center;

  ${props =>
    props.isMinimized &&
    css`
      position: fixed;
      bottom: 271px;
      right: 10px;
    `}
`;

export const OverlayIcon = styled.div`
  background-color: ${props => props.theme.colors.black[80]};
  cursor: pointer;
  margin-left: 5px;
  display: flex;
  align-items: center;
  padding: 2px;
`;

export const MinimizedVideoContainer = styled.div`
  width: 420px;
  height: 260px;
  position: fixed;
  bottom: 10px;
  right: 10px;
`;

export const IFrameContainer = styled.div`
  height: 100vh;
  width: 100vw;
  box-sizing: border-box;
  margin: 0;
`;
